import { isEmptyRichHtml } from '../richHtmlContentShared';
import { normalizeRichListStructure } from './sanitizePaste';

/** Comparable plain text for append detection (assist insert extends the field). */
export function richHtmlPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/**
 * True when `next` is the previous document plus appended plain text (assist
 * insert). Stale parent values are shorter or unrelated, so they fail this.
 */
export function isRichHtmlAppendOf(previous: string, next: string): boolean {
  if (isEmptyRichHtml(previous)) {
    return !isEmptyRichHtml(next);
  }
  if (isEmptyRichHtml(next) || previous === next) {
    return false;
  }
  const prevPlain = richHtmlPlainText(previous);
  const nextPlain = richHtmlPlainText(next);
  return (
    prevPlain.length > 0 &&
    nextPlain.length > prevPlain.length &&
    nextPlain.startsWith(prevPlain)
  );
}

/**
 * Native 10tap often boots a blank WebView even when `initialHtml` was passed
 * as `initialContent` (extra WKWebViews on later steps). `lastEmitted` still
 * holds the parent HTML so we must not emit — only push into the WebView.
 *
 * Never do this after the user has tapped the field: empty then is a clear.
 */
export function shouldForceHydrateBlankEditor(args: {
  isEditorReady: boolean;
  userHasArmedEditor: boolean;
  currentHtml: string;
  incomingHtml: string;
}): boolean {
  if (!args.isEditorReady || args.userHasArmedEditor) {
    return false;
  }
  if (isEmptyRichHtml(args.incomingHtml)) {
    return false;
  }
  return isEmptyRichHtml(args.currentHtml);
}

export function shouldReplaceEditorHtml(args: {
  isFocused: boolean;
  currentHtml: string;
  incomingHtml: string;
  lastEmittedHtml: string;
}): boolean {
  if (args.incomingHtml === args.lastEmittedHtml) {
    return false;
  }
  const next =
    normalizeRichListStructure(args.incomingHtml, {
      preserveEmptyBlocks: true,
    }) || '<p></p>';
  if (args.currentHtml === next) {
    return false;
  }
  if (args.isFocused) {
    // Assist insert into an empty / placeholder surface.
    if (
      isEmptyRichHtml(args.currentHtml) &&
      !isEmptyRichHtml(args.incomingHtml)
    ) {
      // After save, the parent still holds image-only HTML. Backspace empties
      // the editor; this path must not put the photo back (1106 #30). Assist
      // examples have visible text. Image-only incoming does not.
      if (richHtmlPlainText(args.incomingHtml).length === 0) {
        return false;
      }
      return true;
    }
    // Assist append while focused (typed then insert, or second example).
    if (isRichHtmlAppendOf(args.lastEmittedHtml, args.incomingHtml)) {
      return true;
    }
    return false;
  }
  return true;
}
